import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquare, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import usePageTitle from "@/hooks/usePageTitle";

const CATEGORIES = [
  { value: "feature_request", label: "Feature Request", emoji: "✨" },
  { value: "bug_report", label: "Bug Report", emoji: "🐛" },
  { value: "improvement", label: "Improvement", emoji: "🚀" },
  { value: "question", label: "Question", emoji: "❓" },
  { value: "other", label: "Other", emoji: "💬" }
];

const DEPARTMENTS = [
  { value: "Kitchen", label: "Kitchen" },
  { value: "Bar", label: "Bar" },
  { value: "Lab", label: "Lab (Brewery/Fermentation)" },
  { value: "Bakery", label: "Bakery" },
  { value: "Pastry", label: "Pastry/Desserts" },
  { value: "General", label: "General" }
];

const FeedbackPage = ({ settings }) => {
  usePageTitle("pageTitles.feedback");
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    user_email: "",
    phone_number: "",
    category: "feature_request",
    department: "General",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Auto-fill email/phone from settings if available
  useEffect(() => {
    if (settings) {
      setFormData(prev => ({
        ...prev,
        phone_number: settings.phone || ""
      }));
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.user_email || !formData.message) {
      toast.error("Please provide email and message");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, formData);
      toast.success("Feedback submitted successfully! Thank you.");
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          user_email: "",
          phone_number: settings?.phone || "",
          category: "feature_request",
          department: "General",
          message: ""
        });
        setSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-12 shadow-xl shadow-amber-500/5">
            <CheckCircle2 className="h-20 w-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">{t('feedback.thankYou')}</h2>
            <p className="text-lg text-slate-300 mb-6">
              {t('feedback.submittedSuccess')}
            </p>
            <p className="text-sm text-slate-400">
              {t('feedback.redirecting')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="max-w-3xl mx-auto" style={{ fontFamily: i18n.language === 'ar' ? "'Tajawal', sans-serif" : undefined }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-manrope mb-2">
            {t('feedback.title')}
          </h1>
          <p className="text-slate-400 text-lg">
            {t('feedback.subtitle')}
          </p>
        </div>

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} className="bg-slate-900/50 border border-slate-800 rounded-lg p-8 space-y-6 shadow-xl shadow-amber-500/5">
          {/* Email */}
          <div>
            <Label htmlFor="email" className="text-slate-300 mb-2 block">
              {t('feedback.email')} *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.user_email}
              onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
              placeholder={t('feedback.emailPlaceholder')}
              className="bg-slate-800 border-slate-700 text-white"
              required
              data-testid="feedback-email"
              dir="ltr"
            />
          </div>

          {/* Phone (Optional) */}
          <div>
            <Label htmlFor="phone" className="text-slate-300 mb-2 block">
              {t('feedback.phone')}
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              placeholder={t('feedback.phonePlaceholder')}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="feedback-phone"
              dir="ltr"
            /></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Category */}
            <div>
              <Label htmlFor="category" className="text-slate-300 mb-2 block">
                {t('feedback.category')} *
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white">
                      {cat.emoji} {t(`feedback.categories.${cat.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Department */}
            <div>
              <Label htmlFor="department" className="text-slate-300 mb-2 block">
                {t('feedback.departmentContext')}
              </Label>
              <Select
                value={formData.department}
                onValueChange={(value) => setFormData({ ...formData, department: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" id="department">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept.value} value={dept.value} className="text-white">
                      {t(`feedback.departments.${dept.value.toLowerCase()}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-2">
                {t('feedback.departmentHint')}
              </p>
            </div>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="message" className="text-slate-300 mb-2 block">
              {t('feedback.message')} *
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={8}
              className="bg-slate-800 border-slate-700 text-white resize-none"
              placeholder={t('feedback.messagePlaceholder')}
              required
              data-testid="feedback-message"
            />
            <p className="text-xs text-slate-500 mt-2">
              {t('feedback.messageHint')}
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold py-6 text-lg"
            data-testid="submit-feedback"
          >
            {submitting ? (
              <span className="flex items-center gap-2 justify-center">
                <MessageSquare className="h-5 w-5 animate-pulse" />
                {t('feedback.submitting')}
              </span>
            ) : (
              <span className="flex items-center gap-2 justify-center">
                <Send className={`h-5 w-5 ${i18n.language === 'ar' ? 'scale-x-[-1]' : ''}`} />
                {t('feedback.submit')}
              </span>
            )}
          </Button>
        </form>

        {/* Footer Note */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>
            {t('feedback.footerNote')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
